import { useState } from "react";
import { useScrollAnimation } from "../../../hooks/useScrollAnimation";

const faqs = [
  {
    q: "How much does a small business website cost?",
    a: "Our <strong>affordable web design</strong> packages for small businesses start from £795 for a 5-page website. The exact price depends on the size of your site, the features you need, and whether you want ongoing SEO support. We always provide a clear, upfront quote with no hidden fees.",
  },
  {
    q: "How long does it take to build a website?",
    a: "Most small business websites are completed within 10–14 working days from the moment we have all your content. Complex sites or e-commerce projects may take 3–4 weeks. We'll always give you a realistic timeline before we start.",
  },
  {
    q: "Will my website rank on Google?",
    a: "Every website we build is built with on-page <strong>SEO for small businesses</strong> baked in from day one — proper page titles, meta descriptions, structured headings, fast load times, and mobile optimisation. For stronger rankings, we also offer dedicated SEO packages.",
  },
  {
    q: "Do I need to provide the content?",
    a: "You know your business best, so your input is helpful — but we can help you write compelling copy if needed. We'll guide you through exactly what we need and make the process as easy as possible.",
  },
  {
    q: "Will my website work on mobile?",
    a: "Absolutely. Every site we build is fully <strong>responsive design</strong> — it looks and works perfectly on mobiles, tablets, and desktops. With over 60% of searches on mobile, this is non-negotiable for us.",
  },
  {
    q: "Can you redesign my existing website?",
    a: "Yes — website redesign is one of our most popular services. We'll assess your current site, understand what&apos;s not working, and rebuild it as a modern, fast, and conversion-focused platform.",
  },
];

export default function FAQSection() {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const { ref, visible } = useScrollAnimation();

  return (
    <section className="section-pad bg-stone-50" ref={ref}>
      <div className="container-md max-w-3xl">
        <div
          className={`text-center mb-12 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <span className="text-accent text-xs font-semibold uppercase tracking-[0.2em] mb-4 block">
            FAQs
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-zinc-900">
            Frequently Asked Questions
          </h2>
          <p className="text-zinc-500 mt-4 text-base">
            Still have questions? <a href="/contact" className="text-accent hover:underline cursor-pointer">Get in touch</a> — we&apos;re always happy to chat.
          </p>
        </div>

        <div
          className={`space-y-3 transition-all duration-700 delay-200 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          {faqs.map((faq, i) => (
            <div key={i} className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
              <button
                onClick={() => setOpenIdx(openIdx === i ? null : i)}
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer hover:bg-zinc-50 transition-colors"
              >
                <h3 className="font-heading font-semibold text-zinc-900 text-base">{faq.q}</h3>
                <span
                  className={`w-6 h-6 flex items-center justify-center text-accent shrink-0 transition-transform duration-300 ${
                    openIdx === i ? "rotate-45" : ""
                  }`}
                >
                  <i className="ri-add-line text-xl" />
                </span>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIdx === i ? "max-h-48 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <p
                  className="px-6 pb-5 text-zinc-500 text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: faq.a }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Schema.org FAQ structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.a.replace(/<[^>]*>/g, ""),
              },
            })),
          }),
        }}
      />
    </section>
  );
}
